import { MAX_BUFFER_SEGMENTS } from '@ohmyprime/p2p-shared';

/**
 * In-memory LRU cache for segments (encrypted bytes, keyed by rendition:id#sha256).
 */
export class SegmentStore {
  private readonly store: Map<string, ArrayBuffer> = new Map();
  private readonly createdAt = new Map<string, number>();
  private bytes = 0;
  private readonly maxSegments: number;
  private readonly maxBytes: number;
  private readonly maxAgeMs: number;
  private readonly now: () => number;

  constructor(maxSegments = MAX_BUFFER_SEGMENTS, options: { maxBytes?: number; maxAgeMs?: number; now?: () => number } = {}) {
    this.maxSegments = maxSegments;
    this.maxBytes = options.maxBytes ?? 64 * 1024 * 1024;
    this.maxAgeMs = options.maxAgeMs ?? 30_000;
    this.now = options.now ?? Date.now;
  }

  has(segmentId: string): boolean {
    this.expire();
    return this.store.has(segmentId);
  }

  get(segmentId: string): ArrayBuffer | undefined {
    this.expire();
    const data = this.store.get(segmentId);
    if (data !== undefined) {
      // Refresh LRU position by re-inserting
      this.store.delete(segmentId);
      this.store.set(segmentId, data);
    }
    return data;
  }

  set(segmentId: string, data: ArrayBuffer): void {
    this.expire();
    if (data.byteLength > this.maxBytes) return;
    const born = this.createdAt.get(segmentId) ?? this.now();
    this.delete(segmentId);
    this.store.set(segmentId, data);
    this.createdAt.set(segmentId, born);
    this.bytes += data.byteLength;
    this.evictIfNeeded();
  }

  delete(segmentId: string): void {
    this.bytes -= this.store.get(segmentId)?.byteLength ?? 0;
    this.store.delete(segmentId);
    this.createdAt.delete(segmentId);
  }

  private evictIfNeeded(): void {
    while (this.store.size > this.maxSegments || this.bytes > this.maxBytes) {
      const oldest = this.store.keys().next().value;
      if (oldest !== undefined) {
        this.delete(oldest);
      } else {
        break;
      }
    }
  }

  size(): number {
    this.expire();
    return this.store.size;
  }

  clear(): void {
    this.store.clear();
    this.createdAt.clear();
    this.bytes = 0;
  }

  private expire(): void {
    const now = this.now();
    for (const [id, born] of this.createdAt) {
      if (now - born >= this.maxAgeMs) this.delete(id);
    }
  }
}
